import React from "react";
import { Box, Typography, Grid, Tooltip, Fab } from "@mui/material";
import NightSelector from "./NightSelector";
import EquipmentCard from "./EquipmentCard";
import TargetInfoCard from "./TargetInfoCard";
import AcquisitionSummaryCard from "./AcquisitionSummaryCard";
import TabBar from "./TabBar";
import OverviewTabPanel from "./OverviewTabPanel";
import QualityTabPanel from "./QualityTabPanel";
import GuidingTabPanel from "./GuidingTabPanel";
import FrameTabPanel from "./FrameTabPanel";
import { TargetData, ImageMetadata } from "../types";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

interface DetailViewProps {
  selectedTarget: TargetData | null;
  selectedNight: string | "total";
  nightKeys: string[];
  setSelectedNight: (night: string | "total") => void;
  setTargetLastSelectedNight: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  setEditDialogOpen: (open: boolean) => void;
  confirmDialogRef: React.RefObject<any>;
  setConfirmDialogOpen: (open: boolean) => void;
  imagesForView: ImageMetadata[];
  selectedTab: "overview" | "frame" | "quality" | "guiding";
  setSelectedTab: (tab: "overview" | "frame" | "quality" | "guiding") => void;
  frameTypeFilter: string;
  setFrameTypeFilter: (filter: string) => void;
  filterTypeFilter: string;
  setFilterTypeFilter: (filter: string) => void;
  getNightMeta: any;
  formatMultipleValues: any;
  setSelectedTarget?: (target: TargetData | null) => void;
}

const DetailView: React.FC<DetailViewProps> = ({
  selectedTarget,
  selectedNight,
  nightKeys,
  setSelectedNight,
  setTargetLastSelectedNight,
  setEditDialogOpen,
  confirmDialogRef,
  setConfirmDialogOpen,
  imagesForView,
  selectedTab,
  setSelectedTab,
  frameTypeFilter,
  setFrameTypeFilter,
  filterTypeFilter,
  setFilterTypeFilter,
  getNightMeta,
  formatMultipleValues,
  setSelectedTarget,
}) => (
  <Box
    sx={{
      flex: 1,
      p: { xs: 2, md: 4 },
      overflowY: "visible",
      height: "auto",
      backgroundColor: "background.default",
    }}
  >
    {!selectedTarget ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <Typography variant="h4" color="text.secondary">
          Select a target to view details
        </Typography>
      </Box>
    ) : (
      <>
        {/* Vertical Floating Back Button (FAB style) */}
        {setSelectedTarget && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              height: "100vh",
              width: 56,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
              pointerEvents: "none",
            }}
          >
            <Tooltip title="Back to targets" placement="right">
              <Fab
                color="primary"
                onClick={() => setSelectedTarget(null)}
                sx={{
                  pointerEvents: "auto",
                  boxShadow: 4,
                  bgcolor: "background.paper",
                  color: "text.primary",
                  "&:hover": {
                    bgcolor: "#222",
                  },
                }}
                aria-label="Back to target list"
                size="medium"
              >
                <ArrowBackIcon sx={{ fontSize: 32 }} />
              </Fab>
            </Tooltip>
          </Box>
        )}
        {/* Night Selector - Moved to top */}
        <NightSelector
          selectedTarget={selectedTarget}
          selectedNight={selectedNight}
          nightKeys={nightKeys}
          setSelectedNight={setSelectedNight}
          setTargetLastSelectedNight={setTargetLastSelectedNight}
          setEditDialogOpen={setEditDialogOpen}
          confirmDialogRef={confirmDialogRef}
          setConfirmDialogOpen={setConfirmDialogOpen}
        />
        {/* New Detail View: Card Grid */}
        <Grid container spacing={3} sx={{ mb: 3, alignItems: "stretch" }}>
          {/* Equipment Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <EquipmentCard selectedTarget={selectedTarget} />
          </Grid>
          {/* Target Information Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <TargetInfoCard
              selectedTarget={selectedTarget}
              selectedNight={selectedNight}
              getNightMeta={getNightMeta}
              formatMultipleValues={formatMultipleValues}
            />
          </Grid>
          {/* Acquisition Summary Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <AcquisitionSummaryCard
              imagesForView={imagesForView}
              selectedTarget={selectedTarget}
              selectedNight={selectedNight}
              getNightMeta={getNightMeta}
              formatMultipleValues={formatMultipleValues}
            />
          </Grid>
        </Grid>
        {/* Tab Bar */}
        <TabBar selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
        {/* Tab Content */}
        {selectedTab === "overview" && (
          <OverviewTabPanel imagesForView={imagesForView} />
        )}
        {selectedTab === "quality" && (
          <QualityTabPanel imagesForView={imagesForView} />
        )}
        {selectedTab === "guiding" && (
          <GuidingTabPanel imagesForView={imagesForView} />
        )}
        {selectedTab === "frame" && (
          <FrameTabPanel
            imagesForView={imagesForView}
            frameTypeFilter={frameTypeFilter}
            setFrameTypeFilter={setFrameTypeFilter}
            filterTypeFilter={filterTypeFilter}
            setFilterTypeFilter={setFilterTypeFilter}
          />
        )}
      </>
    )}
  </Box>
);

export default DetailView;
